/** Read-only detail of one extracted draft: flat fields, nested days /
 *  timeslots / activities, photo links, warnings and the raw record JSON.
 *
 *  Crawled photo URLs are deliberately shown as links, never rendered
 *  inline — the console CSP (CloudFront img-src) only allows platform
 *  image hosts, so hotlinked previews would ship as broken images.
 */
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import * as aiImportApi from '@/api/aiImport';
import type { AiImportDocument } from '@/api/aiImport';
import { act, ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { integrationActions, productIdActions, tourActions, type TaskCtx } from '@/features/bgtasks/taskLinks';

import { SourceLink } from './SourceLink';

const SKIP_FIELDS = new Set(['days', 'activities', 'images', 'slug', 'confidence']);

/**
 * Extracted fields are whatever the crawler found, so a flat value earns an
 * action only through an ALLOW LIST by key shape — never by sniffing the
 * string. Anything else stays text (with a copy for the long ones).
 */
function flatFieldActions(key: string, value: unknown): ValueAction[] {
  if (typeof value !== 'string' || !value.trim()) return [];
  const text = value.trim();
  if (/^https?:\/\//i.test(text)) return [act.external(text, 'Open the link'), act.copy(text, 'Copy URL')];
  if (/email/i.test(key) && text.includes('@')) return [act.mailto(text), act.copy(text, 'Copy email')];
  if (/phone|tel/i.test(key)) return [act.tel(text), act.copy(text, 'Copy phone')];
  if (/address|location|meeting_point/i.test(key)) return [act.maps(text), act.copy(text, 'Copy address')];
  if (/place_id/i.test(key)) return [act.copy(text, 'Copy place id')];
  return [];
}

function FlatFields({ fields }: { fields: Record<string, unknown> }) {
  const entries = Object.entries(fields).filter(
    ([key, value]) =>
      !SKIP_FIELDS.has(key) &&
      value != null &&
      (typeof value !== 'object' || Object.keys(value as object).length > 0),
  );
  if (entries.length === 0) return null;
  return (
    <Stack spacing={1}>
      {entries.map(([key, value]) => {
        const actions = flatFieldActions(key, value);
        const text = typeof value === 'object' ? JSON.stringify(value, null, 1) : String(value);
        return (
          <Box key={key}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              {key.replace(/_/g, ' ')}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {actions.length > 0 ? <ValueActions actions={actions}>{text}</ValueActions> : text}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
}

interface DayLike {
  title?: string;
  subtitle?: string;
  description?: string;
  timeslots?: {
    title?: string;
    start_time?: string;
    description?: string;
    activity_slugs?: string[];
  }[];
}

function Days({ days }: { days: DayLike[] }) {
  return (
    <Stack spacing={0.5}>
      {days.map((day, i) => (
        <Box key={i} sx={{ pl: 1, borderLeft: 2, borderColor: 'divider' }}>
          <Typography variant="subtitle2">
            Day {i + 1}: {day.title ?? '—'}
          </Typography>
          {day.subtitle && (
            <Typography variant="caption" color="text.secondary">{day.subtitle}</Typography>
          )}
          {day.description && (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{day.description}</Typography>
          )}
          {(day.timeslots ?? []).map((ts, j) => (
            <Typography key={j} variant="body2" sx={{ pl: 1 }}>
              {ts.start_time ? `${ts.start_time} — ` : '• '}
              {ts.title ?? ''}
              {ts.activity_slugs?.length ? ` (${ts.activity_slugs.join(', ')})` : ''}
            </Typography>
          ))}
        </Box>
      ))}
    </Stack>
  );
}

interface Props {
  taskId: string;
  /** The TASK's tenant — the drawer has no tenant of its own, and the product
   *  and integration links are only resolvable inside it. */
  tenantId: number | null;
  extId: string | null;
  /** The task's uploaded documents — a draft from one links to its download. */
  documents: AiImportDocument[];
  onClose: () => void;
}

export function DraftDetailDrawer({ taskId, tenantId, extId, documents, onClose }: Props) {
  const ctx: TaskCtx = { tenantId };
  const query = useQuery({
    queryKey: ['ai-import', 'draft', taskId, extId],
    queryFn: () => aiImportApi.getDraft(taskId, extId!),
    enabled: Boolean(extId),
    staleTime: Infinity,
  });
  const draft = query.data;
  const days = (draft?.fields.days as DayLike[] | undefined) ?? [];
  const embedded = (draft?.fields.activities as Record<string, unknown>[] | undefined) ?? [];

  return (
    <Drawer
      anchor="right"
      open={Boolean(extId)}
      onClose={onClose}
      // The AppBar sits at zIndex.drawer+1 (AppShell); temporary drawers
      // default to zIndex.drawer, so without this the app bar covers the
      // drawer header (title + close button). Same fix as SessionDetailDrawer.
      sx={{ zIndex: (t) => t.zIndex.modal }}
    >
      <Box sx={{ width: 480, p: 2 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {draft?.name ?? 'Draft'}
          </Typography>
          <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
        </Stack>

        {query.isLoading && <CircularProgress size={24} />}

        {draft && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={draft.kind} variant="outlined" />
              {draft.confidence != null && (
                <Chip size="small" label={`${Math.round(draft.confidence * 100)}% confidence`} variant="outlined" />
              )}
              <SourceLink
                taskId={taskId}
                sourceUrl={draft.source_url}
                documents={documents}
                variant="body2"
              >
                Source page <OpenInNewIcon sx={{ fontSize: 12 }} />
              </SourceLink>
            </Stack>

            {draft.issues.length > 0 && (
              <Alert severity="warning">
                {draft.issues.map((issue, i) => <div key={i}>{issue}</div>)}
              </Alert>
            )}

            {draft.restech_match && (
              <Alert severity="success">
                Bound to{' '}
                <ValueActions actions={integrationActions(draft.restech_match.integration, ctx)}>
                  <strong>{draft.restech_match.integration}</strong>
                </ValueActions>{' '}
                product <strong>{draft.restech_match.product_name}</strong> (
                <ValueActions actions={productIdActions(draft.restech_match.product_id, ctx)}>
                  #{draft.restech_match.product_id}
                </ValueActions>
                {draft.restech_match.account ? ` · ${draft.restech_match.account}` : ''}) —{' '}
                {Math.round(draft.restech_match.score * 100)}%{' '}
                {draft.restech_match.via === 'existing_mapping'
                  ? 'match via an existing tour'
                  : 'name match'}
                . Importing updates that product&apos;s tour instead of creating a new one.
                {/* The matched tour is in the payload and was never rendered:
                    it is the record the import is about to change. */}
                {draft.restech_match.tour_id != null && (
                  <>
                    {' '}
                    <ValueActions actions={tourActions(draft.restech_match.tour_id)}>
                      Tour #{draft.restech_match.tour_id}
                    </ValueActions>
                  </>
                )}
              </Alert>
            )}

            <FlatFields fields={draft.fields} />

            {days.length > 0 && (
              <Accordion defaultExpanded disableGutters variant="outlined">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Days ({days.length})</Typography>
                </AccordionSummary>
                <AccordionDetails><Days days={days} /></AccordionDetails>
              </Accordion>
            )}

            {embedded.length > 0 && (
              <Accordion disableGutters variant="outlined">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Activities ({embedded.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {embedded.map((act, i) => (
                      <Box key={i}>
                        <Typography variant="body2" fontWeight={600}>
                          {String(act.name ?? '—')}
                        </Typography>
                        {typeof act.description === 'string' && (
                          <Typography variant="body2" color="text.secondary">
                            {act.description}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {draft.images.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Photos ({draft.images.length})
                </Typography>
                <Stack spacing={0.5}>
                  {draft.images.map((img, i) => (
                    <Link
                      key={i}
                      href={img.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      noWrap
                    >
                      {img.caption ?? img.url.split('/').pop() ?? img.url}
                    </Link>
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Photos are downloaded to the platform during import.
                </Typography>
              </Box>
            )}

            <Accordion disableGutters variant="outlined">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Raw record</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Outside the summary on purpose: a trigger inside a
                    ButtonBase would nest two interactive controls. */}
                <ValueActions actions={[act.copy(JSON.stringify(draft.fields, null, 2), 'Copy JSON')]}>
                  <Typography variant="caption" color="primary">
                    copy JSON
                  </Typography>
                </ValueActions>
                <Box component="pre" sx={{ m: 0, fontSize: 12, overflow: 'auto' }}>
                  {JSON.stringify(draft.fields, null, 2)}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
